import * as React from "react";
import { IPatient } from "../../../../models/IPatient";
import { Tabs, TabContent, TabPanel, TabLink } from "soprasteria-ui-library";
import { PatientListingHistory } from "../PatientListingHistory";
import { TreatmentReportingTab } from "./TreatmentReportingTab";
import { ReferralTab } from "./ReferralTab";
import { OrthodonticReferralTab } from "./OrthodonticReferralTab";
import { GeneralDentalReferralTab } from "./GeneralDentalReferralTab";
import { EmergencyTreatmentTab } from "./EmergencyTreatmentTab";
import { RiskPotReportTab } from "./RiskPotReportTab";
import { UnlistedNotesTab } from "./UnlistedNotesTab";
import { InternalNotesTab } from "./InternalNotesTab";
import { PatientAttachmentsTab } from "./PatientAttachmentsTab";
import { OrthodonticLkgCfPatientExtensionsTab } from "./OrthodonticLkgCfPatientExtensionsTab";
import { OrthodonticLkgCfPatientsTab } from "./OrthodonticLkgCfPatientsTab";
import { PatientContactedTreatmentTab } from "./PatientContactedTreatmentTab";
import { isInRole } from "../../../../contexts/UserContext";

interface IPatientTabs {
    patient: IPatient,
    isInRoleSpecialist: boolean,
    isInRoleSysadmin: boolean,
    isInRoleAllmantandvard: boolean;
    isInRoleParaply: boolean;
    hasRoleKonsulttandläkare: boolean;
    hasRoleOrtodontist: boolean;
    hasRoleLkgCf: boolean;
    reloadData: () => void;
    isLoading: (loading: boolean) => void;
    renderTabs: boolean;
}

export const PatientTabs: React.FunctionComponent<IPatientTabs> = ({ patient, isInRoleSpecialist, isInRoleSysadmin, isInRoleAllmantandvard, isInRoleParaply, hasRoleOrtodontist, hasRoleKonsulttandläkare, hasRoleLkgCf, reloadData, isLoading, renderTabs }) => {

    const renderListingHistory = (): boolean | undefined => {
        return isInRoleSysadmin || isInRoleAllmantandvard;
    }

    const renderTreatmentReport = (): boolean | undefined => {
        return isInRoleSysadmin || (isInRoleAllmantandvard && patient.listingStatus.isPatientListedOnCurrentCareProvider || patient.listingStatus.isPatientListedOnCareProviderUnderUmbrella);
    }
    const tretmentReportIsSelected = (): boolean | undefined => {
        return (!patient.isUnlistedNotesVisible && isInRoleAllmantandvard && patient?.listingStatus.isPatientListedOnCurrentCareProvider) || isInRoleSysadmin;
    }

    const renderReferrals = (): boolean | undefined => {
        return isInRoleSysadmin || isInRoleSpecialist || hasRoleLkgCf || (patient.referral.canCreateNewReferral || patient.referral.referrals.length > 0);
    }

    const renderOrthodonticReferrals = (): boolean | undefined => {
        return (isInRoleSysadmin || hasRoleKonsulttandläkare || hasRoleOrtodontist || (patient.orthodonticReferral.canCreateNewOrthodonticReferral || patient.orthodonticReferral.orthodonticReferrals.length > 0)) && !isInRoleSpecialist;
    }

    const renderGeneralDentalReferrals = (): boolean | undefined => {
        return patient.listingStatus.isPatientListedOnCurrentCareProvider || patient.generalDentalReferral.canCreateNewGeneralDentalReferral || patient.generalDentalReferral.generalDentalReferrals.length > 0;
    }

    const renderEmergencyTreatment = (): boolean | undefined => {
        return isInRoleSysadmin || isInRoleAllmantandvard;
    }
    const emergencyTretmentIsSelected = (): boolean | undefined => {
        return isInRoleAllmantandvard && !patient?.listingStatus.isPatientListedOnCurrentCareProvider && !patient.isUnlistedNotesVisible;
    }

    const renderRiskPotReports = (): boolean | undefined => {
        return isInRoleSysadmin || patient?.riskPotReport.canViewRiskPotReport;
    }

    const patientUnlistedNotesIsSelected = (): boolean | undefined => {
        return !patient?.listingStatus.isPatientListedOnCurrentCareProvider && !isInRoleSysadmin && !isInRoleSpecialist;
    }

    const renderPatientInternalNotes = (): boolean | undefined => {
        return isInRoleSysadmin && patient?.isInternalNotesVisible;
    }

    const renderPatientAttachments = (): boolean | undefined => {
        return isInRoleSysadmin;
    }

    const renderOrthodonticLkgCfPatientExtension = (): boolean | undefined => {
        return patient?.orthodonticLkgCfPatientExtension.isUserAllowedToAccessOrthodonticLkgCfPatientExtensions;
    }

    const renderOrthodonticLkgCfPatients = (): boolean | undefined => {
        return isInRoleSysadmin || hasRoleLkgCf;
    }

    const renderPatientContactedTreatments = (): boolean | undefined => {
        return patient?.patientContactedTreatment.canCreatePatientContactedTreatment || patient?.patientContactedTreatment.patientContactedTreatments.length > 0;
    }

    return (
        <section>
            {renderTabs ?
                <Tabs>
                    <TabLink href="treatmentReports" id="treatmentReportTab" text="Vårdrapport" selected={tretmentReportIsSelected()} render={renderTreatmentReport()} />

                    <TabLink href="listingHistory" id="listingHistoryTab" text="Listningar" render={renderListingHistory()} />

                    <TabLink href="referrals" id="referallsTab" text="Specialistremiss" selected={isInRoleSpecialist} render={renderReferrals()} />

                    <TabLink href="orthodonticReferals" id="orthodonticReferalsTab" text="Ortodontiremiss" render={renderOrthodonticReferrals()} selected={hasRoleOrtodontist || hasRoleKonsulttandläkare} />

                    <TabLink href="generalDentalReferals" id="generalDentalReferalsTab" text="Allmäntandvårdsremiss" render={renderGeneralDentalReferrals()} />

                    <TabLink href="emergencyTreatment" id="emergencyTreatmentTab" text="Akuträkning" selected={emergencyTretmentIsSelected()} render={renderEmergencyTreatment()} />

                    <TabLink href="riskPotReports" id="riskPotReportsTab" text="Riskpott" render={renderRiskPotReports()} />

                    <TabLink href="notes" id="notesTab" text="Noteringar" selected={patientUnlistedNotesIsSelected()} render={patient.isUnlistedNotesVisible} />

                    <TabLink href="internalNotes" id="internalNotesTab" text="TVE-noteringar" render={renderPatientInternalNotes()} />

                    <TabLink href="patientAttachments" id="patientAttachmentsTab" text="TVE-dokument" render={renderPatientAttachments()} />

                    <TabLink href="orthodonticLkgCfPatientExtensions" id="orthodonticLkgCfPatientExtensionsTab" text="Förlängning av LKG/CF" render={renderOrthodonticLkgCfPatientExtension()} />

                    <TabLink href="orthodonticLkgCfPatients" id="orthodonticLkgCfPatientsTab" text="LKG/CF-registreringar" render={renderOrthodonticLkgCfPatients()} />

                    <TabLink href="patientContactedTreatments" id="patientContactedTreatmentsTab" text="Patientkontakter" render={renderPatientContactedTreatments()} />
                </Tabs> : null
            }
            {renderTabs ?
                <TabContent>
                    <TabPanel id="treatmentReports" ariaLabelledBy="treatmentReportTab" selected={tretmentReportIsSelected()} render={renderTreatmentReport()}>
                        <TreatmentReportingTab treatmentReports={patient.treatmentReporting.treatmentReports} canCreateNewTreatmentReport={patient.treatmentReporting.canCreateNewTreatmentReport} patientId={patient.patientId} />
                    </TabPanel>

                    <TabPanel id="listingHistory" ariaLabelledBy="listingHistoryTab" render={renderListingHistory()}>
                        <PatientListingHistory listings={patient.listings} />
                    </TabPanel>

                    <TabPanel id="referrals" ariaLabelledBy="referallsTab" selected={isInRoleSpecialist || hasRoleLkgCf} render={renderReferrals()}>
                        <ReferralTab canCreateNewReferral={patient.referral.canCreateNewReferral} canCreateNewReferralManualRegistration={patient.referral.canCreateNewReferralManualRegistration} referrals={patient.referral.referrals} isInRoleAllmäntandvård={isInRoleAllmantandvard} reloadData={reloadData} isLoading={isLoading} />
                    </TabPanel>

                    <TabPanel id="orthodonticReferals" ariaLabelledBy="orthodonticReferalsTab" render={renderOrthodonticReferrals()} selected={hasRoleOrtodontist || hasRoleKonsulttandläkare}>
                        <OrthodonticReferralTab canCreateNewOrthodonticReferral={patient.orthodonticReferral.canCreateNewOrthodonticReferral} isInRoleAllmäntandvård={isInRoleAllmantandvard} orthodonticReferrals={patient.orthodonticReferral.orthodonticReferrals} isRoleOrthodontist={hasRoleOrtodontist} isPatientEntitledToOrthodonticCare={patient.orthodonticReferral.isPatientEntitledToOrthodonticCare} patientNotEntitledToOrthodonticCareReason={patient.orthodonticReferral.patientNotEntitledToOrthodonticCareReason} reloadData={reloadData} isLoading={isLoading}  />
                    </TabPanel>

                    <TabPanel id="generalDentalReferals" ariaLabelledBy="generalDentalReferalsTab" render={renderGeneralDentalReferrals()}>
                        <GeneralDentalReferralTab canCreateNewGeneralDentalReferral={patient.generalDentalReferral.canCreateNewGeneralDentalReferral} isInRoleAllmäntandvård={isInRoleAllmantandvard} isInRoleOrtodontist={hasRoleOrtodontist} generalDentalReferrals={patient.generalDentalReferral.generalDentalReferrals} reloadData={reloadData} isLoading={isLoading} />
                    </TabPanel>

                    <TabPanel id="emergencyTreatment" ariaLabelledBy="emergencyTreatmentTab" selected={emergencyTretmentIsSelected()} render={renderEmergencyTreatment()}>
                        <EmergencyTreatmentTab emergencyTreatmentReports={patient.emergencyTreatment} patientId={patient.patientId} reloadData={reloadData} />
                    </TabPanel>

                    <TabPanel id="riskPotReports" ariaLabelledBy="riskPotReportsTab" render={renderRiskPotReports()}>
                        <RiskPotReportTab riskPotReports={patient.riskPotReport.riskPotReports} canCreateRiskPotReport={patient.riskPotReport.canCreateRiskPotReport} patientId={patient.patientId} minimumStartDate={patient.riskPotReport.minimumStartDate} reloadData={reloadData} />
                    </TabPanel>

                    <TabPanel id="notes" ariaLabelledBy="notesTab" selected={patientUnlistedNotesIsSelected()} render={patient.isUnlistedNotesVisible}>
                        <UnlistedNotesTab pnr={patient.personalSecurityNumber} isInRoleAllmantandvard={isInRoleAllmantandvard} isInRoleParaply={isInRoleParaply} patientId={patient.patientId} />
                    </TabPanel>

                    <TabPanel id="internalNotes" ariaLabelledBy="internalNotesTab" render={renderPatientInternalNotes()}>
                        <InternalNotesTab patientId={patient.patientId} />
                    </TabPanel>

                    <TabPanel id="patientAttachments" ariaLabelledBy="patientAttachmentsTab" render={renderPatientAttachments()}>
                        <PatientAttachmentsTab patientId={patient.patientId} />
                    </TabPanel>

                    <TabPanel id="orthodonticLkgCfPatientExtensions" ariaLabelledBy="orthodonticLkgCfPatientExtensionsTab" render={renderOrthodonticLkgCfPatientExtension()}>
                        <OrthodonticLkgCfPatientExtensionsTab canCreateOrthodonticLkgCfPatientExtension={patient.orthodonticLkgCfPatientExtension.canCreateOrthodonticLkgCfPatientExtension}
                            orthodonticLkgCfPatientExtensions={patient.orthodonticLkgCfPatientExtension.orthodonticLkgCfPatientExtensions} />
                    </TabPanel>

                    <TabPanel id="orthodonticLkgCfPatients" ariaLabelledBy="orthodonticLkgCfPatientsTab" render={renderOrthodonticLkgCfPatients()}>
                        <OrthodonticLkgCfPatientsTab orthodonticLkgCfPatients={patient.orthodonticLkgCfPatients} />
                    </TabPanel>

                    <TabPanel id="patientContactedTreatments" ariaLabelledBy="patientContactedTreatmentsTab" render={renderPatientContactedTreatments()}>
                        <PatientContactedTreatmentTab patientContactedTreatment={patient.patientContactedTreatment} patientId={patient.patientId} reloadData={reloadData} />
                    </TabPanel>
                </TabContent> : null
            }
        </section>
    )
}